<script lang="ts">
import { computed } from 'vue';
import { LazyCollectionSelect } from '#layers/data-model/lazy';
import { isEmptyFilter, isLogicalFilter, useCollection } from '#layers/data-model/public';
import { FilterGateMode, FilterLogicalGroup, provideFilterGate, useLogicalFilter } from '#layers/filters/public';

const __DsBlock = {
	name: '$t:blocks.input_filter.name',
	description: '$t:blocks.input_filter.description',
	icon: 'lucide:filter',
	type: 'input',
	category: 'input',
	hidden: true,
	options: [
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
			model: true,
		},
		{
			name: 'collectionId',
			label: '$t:blocks.common.props.collection',
			render: LazyCollectionSelect,
		},
		{
			name: 'clearFilterDisplayStyle',
			type: 'enum',
			values: ['none', 'link'],
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
	],
}</script>

<script setup lang="ts">
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { Filter, GlobalCollectionId, LogicalFilter } from '#layers/data-model/public';
import type { ClearFilterDisplayStyle } from '#layers/filters/public';
import { usePermissionsStore } from '#layers/access/public';
import { defineBlock } from '#layers/block-engine/public';
import { usePrimitiveFormField } from '#layers/forms/public';
import { useDisabledNotice, useReadonly } from '#layers/ui/public';

interface Props extends InputBlockProps {
	collectionId: GlobalCollectionId | null;
	clearFilterDisplayStyle?: ClearFilterDisplayStyle;
	hideBorder?: boolean;
	enableVariables?: boolean;
	/** `schema` for policy rules, which are evaluated for other users; `full` for the current user's queries. */
	gate: Exclude<FilterGateMode, typeof FilterGateMode.None>;
}

const {
	clearFilterDisplayStyle = 'none',
	collectionId,
	gate,
	readonly,
	disabled: disabledProp,
	disabledNotice: disabledNoticeProp,
} = defineProps<Props>();
const model = defineModel<Filter | null>();

const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(() => ({ disabled: disabledProp, disabledNotice: disabledNoticeProp }), { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const { collection } = useCollection(() => collectionId);
const permissionsStore = usePermissionsStore();

// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- the provider selects its gating mode at mount; its collection and permission checks stay reactive
provideFilterGate(gate === FilterGateMode.Schema
	? { mode: FilterGateMode.Schema, collection }
	: { mode: FilterGateMode.Full, collection, canReadField: permissionsStore.canReadField });

const filter = computed({
	get(): LogicalFilter {
		if (!modelValue.value || isEmptyFilter(modelValue.value)) {
			return { _and: [] };
		}
		else if (isLogicalFilter(modelValue.value)) {
			return modelValue.value;
		}
		else {
			return { _and: [modelValue.value] };
		}
	},
	set(newFilter: LogicalFilter) {
		if (!newFilter || isEmptyFilter(newFilter)) {
			modelValue.value = null;
		}
		else {
			modelValue.value = newFilter;
		}
	},
});
const { handleClearFilter } = useLogicalFilter(filter);

;

const { preventKeyboardNavigation, preventPointer } = useReadonly(() => readonly);
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<div class="flex flex-col gap-0.5">
			<div
				v-if="readonly && isEmptyFilter(filter)"
				class="flex items-center gap-2 text-sm text-muted"
			>
				<DIcon name="lucide:list-filter" />
				<span>{{ $t('blocks.input_filter.empty_readonly') }}</span>
			</div>
			<FilterLogicalGroup
				v-else-if="collection"
				v-model="filter"
				class="rounded-md bg-default px-3 py-2.5 text-default"
				:disabled
				:class="{ 'cursor-not-allowed': disabled, 'border-none': hideBorder, 'border': !hideBorder, 'pointer-events-none': readonly }"
				:collection
				:hide-group-actions="false"
				:clear-filter-display-style
				:enable-variables
				hide-outer-group
				:readonly
				:aria-readonly="readonly"
				@pointerdown.capture="preventPointer"
				@click.capture="preventPointer"
				@keydown.capture="preventKeyboardNavigation"
			/>
			<DButton
				v-if="clearFilterDisplayStyle === 'link' && !isEmptyFilter(filter) && !readonly"
				size="xs"
				:label="$t('filters.clear_filter')"
				class="self-start text-dimmed"
				color="neutral"
				variant="link"
				@click="handleClearFilter"
			/>
		</div>
	</DTooltip>
</template>
